import React, {
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  useEffect,
} from 'react';
import PropTypes from 'prop-types';
import * as Styled from './RecordingButton.styled';
import { Icon, Loading } from '@virtidev/toolbox';
import { MicrophoneStreamContext } from '../../../../../core/src/components/MicrophoneStreamProvider/MicrophoneStreamProvider';
import MutedNotification from '../MutedNotification/MutedNotification';
import RecordingPopovers from '@vh/components/VHChatInputArea/RecordingButton/RecordingIssuePopovers/RecordingPopovers';
import { MicBehaviorContext } from '@vh/components/VHChatInputArea/MicBehaviorProvider.context';
import useMicStreamVolume from '@core/components/MicrophoneStreamProvider/useMicStreamVolume';
import { ThemeContext } from 'styled-components';
import { useTranslation } from 'react-i18next';

const RecordingButton = ({
  disabled,
  onClick,
  muted,
  connected,
  showMutedNoticeIfTalked,
  networkError,
  recording,
  connecting,
  design,
  loading,
  onConnectionChange,
}) => {
  const { error: micError, stream } = useContext(MicrophoneStreamContext);
  /** @type {React.RefObject<HTMLButtonElement> | null} */
  const recordBtnRef = useRef(null);

  const [showMicUnavailable, setShowMicUnavailable] = useState(false);

  // keep track of muted count so we can remount the muted notification
  const [mutedCount, setMutedCount] = useState(1);

  const buttonDisabled = disabled || loading;

  const { micBehavior } = useContext(MicBehaviorContext);

  // use clickChangesMuteTo to determine what the button will do when clicked
  // group with icon to avoid having to repeat the logic
  const stateData = useMemo(() => {
    let result;
    if (buttonDisabled) {
      result = { icon: 'microphoneoff', clickChangesMuteTo: null };
    } else if (micBehavior === 'record_and_send' && recording) {
      result = { icon: 'stop', clickChangesMuteTo: true };
    } else if (micBehavior === 'open' && (connected || connecting)) {
      result = { icon: 'stop', clickChangesMuteTo: true };
    } else if (micBehavior === 'push_to_talk' && recording) {
      result = { icon: 'stop', clickChangesMuteTo: true };
    } else {
      result = { icon: 'microphone', clickChangesMuteTo: false };
    }
    
    // Debug logging
    console.log('🎤 RecordingButton stateData calculated:', {
      buttonDisabled,
      micBehavior,
      recording,
      connected,
      connecting,
      muted,
      result
    });
    
    return result;
  }, [buttonDisabled, recording, connected, connecting, micBehavior, muted]);
  const micIcon = stateData.icon;

  const handleOnClick = useCallback(async () => {
    console.log('🎤 RecordingButton clicked! Current state:', {
      buttonDisabled,
      micBehavior,
      recording,
      connected,
      connecting,
      muted,
      clickChangesMuteTo: stateData.clickChangesMuteTo,
      micError: !!micError
    });
    
    setMutedCount((curCount) => curCount + 1);
    let micPermissions = true;
    // code defensively against permissions check potentially not working in firefox
    // (though works in mine now so seems to have passed experimental and not yet documented as more than "nightly" version supported)
    try {
      const micPermissionQuery = await navigator.permissions.query({
        name: 'microphone',
      });
      if (micPermissionQuery.state !== 'granted') {
        micPermissions = false;
      }
    } catch (e) {
      console.error(e);
    }
    if (micError || !micPermissions) {
      console.log('🎤 RecordingButton: Showing mic unavailable popup', { micError: !!micError, micPermissions });
      setShowMicUnavailable(true);
    } else {
      console.log('🎤 RecordingButton: Calling onClick with clickChangesMuteTo =', stateData.clickChangesMuteTo);
      onClick(stateData.clickChangesMuteTo);
    }
  }, [micError, onClick, stateData.clickChangesMuteTo, buttonDisabled, micBehavior, recording, connected, connecting, muted]);
  // if big UI we want to handle click for shownig mic error

  const micVolume = useMicStreamVolume(40, !muted);
  const theme = useContext(ThemeContext);
  const { t } = useTranslation();

  useEffect(() => {
    if (onConnectionChange) {
      onConnectionChange(connected);
    }
  }, [connected, onConnectionChange]);

  return (
    <Styled.SpeechToTextWrapper>
      {/* {!connected && (
        <Styled.ConnectionError title="Connection error when connecting to Speech to Text service. Click unmute to retry connection.">
          <Icon icon="error" />
          Error
        </Styled.ConnectionError>
      )} */}
      <RecordingPopovers
        showMicUnavailable={showMicUnavailable}
        setShowMicUnavailable={setShowMicUnavailable}
        attachToRef={recordBtnRef}
        networkError={networkError}
      />
      <Styled.RecordingButton
        $muted={(muted || !connected) && !recording}
        disabled={buttonDisabled}
        onClick={handleOnClick}
        $micVolume={micVolume}
        design={micIcon === 'stop' ? null : design}
        ref={recordBtnRef}
        $micBehavior={micBehavior}
        aria-label={t('toggleMicRecord')}
        style={{
          outline:
            !muted &&
            !!stream &&
            micVolume > 0 &&
            (micBehavior === 'open' || recording) &&
            !buttonDisabled
              ? `${Math.min(Math.max(micVolume * 50 - 0.3, 0), 6)}px solid ${
                  theme.color.turquoise20
                }`
              : 'none',
        }}
      >
        {loading && <Loading />}
        {!loading && micIcon === 'stop' && <Styled.StopIcon></Styled.StopIcon>}
        {!loading && micIcon !== 'stop' && (
          <Icon icon={micIcon} size="1.4rem" type={'outline'} />
        )}
      </Styled.RecordingButton>
      {/* <VolumeMeter disabled={disabled} active={!muted} /> */}
      <MutedNotification
        showMutedNoticeIfTalked={showMutedNoticeIfTalked}
        active={!muted}
        key={mutedCount}
      />
    </Styled.SpeechToTextWrapper>
  );
};

RecordingButton.propTypes = {
  disabled: PropTypes.bool.isRequired,
  muted: PropTypes.bool.isRequired,
  onClick: PropTypes.func,
  onMicUnavailable: PropTypes.func,
  showMutedNoticeIfTalked: PropTypes.bool,
  networkError: PropTypes.bool,
  connecting: PropTypes.bool.isRequired,
  onConnectionChange: PropTypes.func,
};

export default RecordingButton;
